import moment from 'moment';
import { createSelector } from 'reselect';

import { surveyResponsesForSurveyIdSelector } from '../../reducers/survey-selectors';

import { EmployeeCanMessage } from '../../util/can-message';
import { employeeName } from '../utils/employee-name';

interface SurveyResponseRow {
  surveyResponseId: number;
  surveyId: number;
  employeeId: number;
  name: string;
  answer: string | null;
  imageUrls: Array<string> | null;
  errorIsDidNotTryToSend: boolean;
  errorSendingMessage: boolean;
  errorIsOptOut: boolean;
  canTextResult: CanTextMessageResult;
  answeredAt: string | undefined;
  retryMessageId: number | undefined;
  isRelevant: boolean;
}

export interface EmployeeSurveyResponse {
  surveyId: number;
  employeeId: number;
  name: string;
  hasAnswer: boolean;
  firstResponseTime: moment.Moment | null;
  responses: Array<SurveyResponseRow>;
  firstSurveyResponseId: number;

  hasError: boolean;
  errorMessage: string | null;
  errorIsFixedByPhoneNumberUpdate: boolean;
  canRetry: boolean;
  retryMessageId: number | undefined;
}

const mapSurveyResponseToSurveyDetailResponse = (
  surveyResponse: SurveyResponse,
): SurveyResponseRow => {
  const { Employee, Response, OutboundMessage, manualResponse, manualResponseSavedAt } =
    surveyResponse;
  const canMessage = new EmployeeCanMessage(Employee);
  return {
    surveyResponseId: surveyResponse.surveyResponseId,
    surveyId: surveyResponse.surveyId,
    employeeId: surveyResponse.employeeId,
    name: employeeName(Employee),

    // Checking for the response being inbound is a backwards-compatibility
    // We used to overwrite the messageId column with the outbound messageId
    answer: manualResponse || (Response?.isInbound ? Response.messageBody : null),
    imageUrls: Response?.imageUrls ? Response.imageUrls.split(',') : null,
    errorIsDidNotTryToSend: surveyResponse.failedToSend,
    errorSendingMessage: Boolean(OutboundMessage?.isFailed),
    errorIsOptOut: Employee.optedOutOfTexts,
    canTextResult: canMessage.canTextMessage(),
    answeredAt: manualResponseSavedAt || Response?.activity.datetime,
    retryMessageId: OutboundMessage?.messageId,
    isRelevant: surveyResponse.isRelevant,
  };
};

const combineSurveyResponsesForEmployees = (
  responseRows: Array<SurveyResponseRow>,
): Array<EmployeeSurveyResponse> => {
  const responseMap: { [employeeId: number]: EmployeeSurveyResponse } = responseRows.reduce(
    (acc, response) => {
      let errorMessage = null;
      const canRetry =
        (response.canTextResult.canText && response.errorIsDidNotTryToSend) ||
        response.errorSendingMessage;
      if (canRetry) {
        errorMessage =
          'This message could not be delivered because the phone is off or out of cell range. Click to retry.';
      } else if (response.errorIsOptOut) {
        errorMessage = 'This employee opted out of receiving texts';
      } else if (!response.canTextResult.canText) {
        errorMessage = `${response.canTextResult.errorMessage} Click to update and retry.`;
      } else if (response.errorIsDidNotTryToSend) {
        errorMessage =
          "This employee's phone number is missing or invalid. Click to update and retry";
      }
      if (!acc[response.employeeId]) {
        acc[response.employeeId] = {
          surveyId: response.surveyId,
          employeeId: response.employeeId,
          name: response.name,
          hasAnswer: Boolean(response.answer || response.imageUrls),
          firstResponseTime:
            (response.answer || response.imageUrls) && response.answeredAt
              ? moment.utc(response.answeredAt)
              : null,
          responses: [response],

          hasError:
            response.errorIsDidNotTryToSend ||
            response.errorSendingMessage ||
            response.errorIsOptOut,
          errorMessage,
          errorIsFixedByPhoneNumberUpdate:
            !response.errorSendingMessage &&
            !response.errorIsOptOut &&
            !response.canTextResult.canText,
          retryMessageId: response.retryMessageId,
          firstSurveyResponseId: response.surveyResponseId,
          canRetry,
        };
      } else {
        const { responses, hasAnswer } = acc[response.employeeId];
        acc[response.employeeId].responses = responses.concat(response);
        acc[response.employeeId].hasAnswer =
          hasAnswer || Boolean(response.answer || response.imageUrls);
        if (
          (response.answer || response.imageUrls) &&
          response.answeredAt &&
          moment.utc(response.answeredAt).isBefore(acc[response.employeeId].firstResponseTime)
        ) {
          acc[response.employeeId].firstResponseTime = moment.utc(response.answeredAt);
        }
      }
      // Sort responses oldest to newest
      acc[response.employeeId].responses.sort((a, b) =>
        moment.utc(a.answeredAt).isBefore(b.answeredAt) ? -1 : 1,
      );
      return acc;
    },
    {} as { [employeeId: number]: EmployeeSurveyResponse },
  );

  return Object.values(responseMap);
};

export const aggregatedSurveyResponsesForSurveyIdSelector = createSelector(
  [surveyResponsesForSurveyIdSelector],
  (surveyResponsesForSurveyIdArray) => {
    const surveyDetailResponses = surveyResponsesForSurveyIdArray.map(
      mapSurveyResponseToSurveyDetailResponse,
    );
    return combineSurveyResponsesForEmployees(surveyDetailResponses);
  },
);
